import React, { useEffect, useState, useMemo, useCallback } from 'react'

import MyLocation from './MyLocation'
import { useAutocompleteStore, useColdStartStore } from '../store'
import DefaultSearches from './DefaultSearches'
import CoincidencesList from './CoincidencesList'
import ZoneCoincidences from './ZoneCoincidences'
import { GoogleMapsStructuredPlace } from '../interfaces/googleMapsInterfaces'
import { composeClasses } from '@wiggot/utils/src/strings'
import { CgSpinner } from 'react-icons/cg'
import { TiDeleteOutline } from 'react-icons/ti'
import { ILocation } from './Autocomplete'

export interface Props {
  /**
   * This property indicates whether the Autocomplete is focused.
   * @type {boolean}
   * @default false
   */
  isFocusedAutocomplete: boolean

  /**
   * This is the className of the Container
   * @type {string}
   * */
  className?: string

  /**
   * This is the className of the Container
   * @type {string}
   * */
  itemClassName?: string

  /**
   * This is the searchText of the SearchField
   * @type {string}
   * */
  searchText: string

  /**
   * This function is called when the match is selected
   * @type {(place: GoogleMapsStructuredPlace) => void}
   * */
  onSelected: (place: GoogleMapsStructuredPlace) => void

  /**
   * This is the myLocation of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableMyLocation?: boolean

  /**
   * This is the defaultSearches of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableDefaultSearches?: boolean

  /**
   * This is the popularSearches of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enablePopularSearches?: boolean

  /**
   * This is the savedSearches of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableSavedSearches?: boolean

  /**
   * This is the zoneSearch of the AutocompleteResults
   * @type {boolean}
   * @default false
   * */
  enableZoneSearches?: boolean

  /**
   * This function is called when the matches are loaded.
   * @type {(hasCoincidences: boolean) => void}
   * */
  onLoadedCoincidences?: (hasCoincidences: boolean) => void

  /**
   * Indicates whether the street number must be validated.
   * @type {boolean}
   */
  validateStreetNumber?: boolean

  /**
   * Assigns default regions.
   * @type {string[]}
   */
  limitedRegions?: string[]

  /**
   * This is the types of the AutocompleteResults
   * @type {string[]}
   * @default ["geocode"]
   */
  placeTypes?: string[]

  /**
   * Boolean to inidicate if is necesary remark the option selected in the coincidences list
   * @type {boolean}
   */
  withRemarkSelected?: boolean

  /**
   * Styles for location's label.
   * @type {string}
   */
  labelClassName?: string

  /**
   * Styles for location's label icon.
   * @type {string}
   */
  iconLabelClassName?: string

  /**
   * @description locations to show in the results
   * @type {ILocation[]}
   */
  locations?: ILocation[]

  /**
   * @description This function is called when the locations delete button is clicked
   * @type {(id:string) => void}
   */
  onDeleteLocation?: (id: string) => void
  remarkSelectedClassNames?: string

  focusOptionClassNames?: string

  itemTextClassName?: string
}

/**
 * This component is responsible for displaying the matches of the search and default suggestions.
 * @param isFocusedAutocomplete - This property indicates whether the Autocomplete is focused.
 * @param className - This is the className of the Container.
 * @param itemClassName - This is the className of the item.
 * @param searchText - This is the searchText of the SearchField.
 * @param onSelected - This function is called when the match is selected.
 * @param visibledSeccions - This is the visibledSeccions of the AutocompleteResults.
 * @param enableMyLocation - This is the myLocation of the AutocompleteResults.
 * @param enableDefaultSearches - This is the defaultSearches of the AutocompleteResults.
 * @param enablePopularSearches - This is the popularSearches of the AutocompleteResults.
 * @param enableSavedSearches - This is the savedSearches of the AutocompleteResults.
 * @param onLoadedCoincidences - This function is called when the matches are loaded.
 * @param validateStreetNumber - Indicates whether the street number must be validated.
 * @param defaultBounds - Assigns default limits.
 * @param limitedCountry - Assigns default country, default "MX".
 * @param limitedRegions - Assigns default regions.
 * @param limitedBounds - Assign default limits, this overrides the "defaultBounds".
 * @param placeTypes - This is the types of the AutocompleteResults.
 * @param withRemarkSelected - boolean to remark a selected option with the searchText.
 * @returns {JSX.Element}
 * */
const ResultsContainer = ({
  isFocusedAutocomplete,
  className,
  itemClassName,
  searchText,
  onSelected,
  onDeleteLocation,
  enableMyLocation = false,
  enableDefaultSearches = false,
  enablePopularSearches = false,
  enableSavedSearches = false,
  enableZoneSearches = false,
  onLoadedCoincidences: triggerOnLoadedCoincidences,
  validateStreetNumber,
  limitedRegions,
  placeTypes,
  withRemarkSelected,
  labelClassName,
  iconLabelClassName,
  remarkSelectedClassNames,
  focusOptionClassNames,
  itemTextClassName,
  locations
}: Props): JSX.Element => {
  const { defaultSearches, isUsedAutocomplete } = useColdStartStore()

  const [isInitialized, setIsInitialized] = useState(false)

  const [hasCoincidences, setHasCoincidences] = useState(false)
  const [zoneServiceReady, setZoneServiceReady] = useState(false)
  const [coincidencesServiceReady, setCoincidencesServiceReady] =
    useState(false)

  const [totalCoincidences, setTotalCoincidences] = useState(0)
  const [totalZones, setTotalZones] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const totalOptions = useMemo(() => {
    if (enableDefaultSearches && !isUsedAutocomplete) {
      return totalCoincidences + totalZones + defaultSearches.length
    }
    return totalCoincidences + totalZones
  }, [
    enableDefaultSearches,
    isUsedAutocomplete,
    totalCoincidences,
    totalZones,
    defaultSearches.length
  ])

  const showResults = useMemo(() => {
    if (
      enableDefaultSearches &&
      !isUsedAutocomplete &&
      defaultSearches.length > 0
    ) {
      return true
    }

    if (enableZoneSearches) {
      if (searchText.length < 4) {
        return zoneServiceReady
      }

      return coincidencesServiceReady && zoneServiceReady
    }

    return coincidencesServiceReady
  }, [
    enableDefaultSearches,
    isUsedAutocomplete,
    defaultSearches.length,
    enableZoneSearches,
    coincidencesServiceReady,
    searchText.length,
    zoneServiceReady
  ])

  const hasAnySectionEnabled = useMemo(() => {
    return (
      enableMyLocation ||
      (enableDefaultSearches && !isUsedAutocomplete) ||
      enablePopularSearches ||
      enableSavedSearches
    )
  }, [
    enableMyLocation,
    enableDefaultSearches,
    isUsedAutocomplete,
    enablePopularSearches,
    enableSavedSearches
  ])

  const isVisibledResultsContainer = useMemo(() => {
    if (!isFocusedAutocomplete) {
      return false
    }

    if (hasAnySectionEnabled) {
      return true
    }

    if (searchText) {
      return true
    }
  }, [isFocusedAutocomplete, hasAnySectionEnabled, searchText])

  /**
   * This function is called when the matches are loaded.
   * @param {boolean} coincidences - Indicates whether there are matches.
   * @returns {void}
   * */
  const onLoadedCoincidences = (coincidences: boolean): void => {
    setHasCoincidences(coincidences)

    setIsInitialized(true)

    if (!searchText) {
      return
    }

    if (triggerOnLoadedCoincidences) {
      triggerOnLoadedCoincidences(coincidences)
    }
  }

  const onLoadedCoincidencesService = (isReady: boolean): void => {
    setCoincidencesServiceReady(isReady)
  }

  const onLoadedZoneCoincidencesService = (isReady: boolean): void => {
    setZoneServiceReady(isReady)
  }

  const handleKeyDown = useCallback(
    async (event: any): Promise<void> => {
      if (
        !totalOptions ||
        (!(isInitialized && Boolean(isVisibledResultsContainer)) &&
          !enableDefaultSearches)
      ) {
        setSelectedIndex(-1)
        return
      }

      if (event.key === 'ArrowDown') {
        setSelectedIndex((prevIndex) => (prevIndex + 1) % totalOptions)
      } else if (event.key === 'ArrowUp') {
        setSelectedIndex(
          (prevIndex) => (prevIndex - 1 + totalOptions) % totalOptions
        )
      }
    },
    [
      totalOptions,
      isInitialized,
      isVisibledResultsContainer,
      enableDefaultSearches
    ]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [totalOptions, selectedIndex, handleKeyDown])

  useEffect(() => {
    if (!searchText) {
      setIsInitialized(false)
    }
  }, [searchText])

  return (
    <div hidden={!isVisibledResultsContainer}>
      <div
        className={`absolute w-[312px] ${
          hasAnySectionEnabled || (!hasAnySectionEnabled && isInitialized)
            ? 'p-4'
            : ''
        } flex flex-col gap-4 bg-white rounded-lg [box-shadow:0px_3px_14px_-3px_rgba(24,26,61,0.12)] ${className}`}
      >
        {locations && locations.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {locations.map(({ id, searchAddress }) => (
              <div
                key={id}
                className="flex items-center px-2 py-1 rounded-full bg-[#F4F7FA] gap-1 w-[105px]"
              >
                <p className="max-w-[69px] truncate text-[10px] font-bold uppercase">
                  {searchAddress}
                </p>
                <button
                  type="button"
                  onClick={() => onDeleteLocation?.(id)}
                  className="w-4 h-4 text-text-primary"
                >
                  <TiDeleteOutline className="text-base" />
                </button>
              </div>
            ))}
          </div>
        )}
        {enableMyLocation ? (
          <div
            className={composeClasses(
              (enableDefaultSearches && !isUsedAutocomplete) ||
                enablePopularSearches ||
                enableSavedSearches ||
                (isInitialized && !hasCoincidences)
                ? 'pb-2 border-b border-[#EBEDF0]'
                : ''
            )}
            hidden={hasCoincidences && isInitialized}
          >
            <MyLocation
              enableDefaultSearches={enableDefaultSearches}
              isVisible={
                !hasCoincidences && Boolean(isVisibledResultsContainer)
              }
              onSelected={(place) => {
                onSelected({
                  ...place,
                  userInput: place?.fullAddress
                })
                setSelectedIndex(-1)
              }}
              labelClassName={labelClassName}
              iconLabelClassName={iconLabelClassName}
            />
          </div>
        ) : null}

        {!showResults && isInitialized && (
          <div className="h-32 w-full flex justify-center items-center">
            <CgSpinner className="animate-spin text-2xl" />
          </div>
        )}

        {enableZoneSearches ? (
          <ZoneCoincidences
            showResults={showResults}
            selectedIndex={selectedIndex}
            hidden={!isInitialized}
            searchText={searchText}
            onSelected={(place) => {
              onSelected({
                ...place,
                userInput: searchText
              })
              setSelectedIndex(-1)
            }}
            withRemarkSelected={withRemarkSelected}
            setTotalZones={setTotalZones}
            itemClassName={itemClassName}
            remarkSelectedClassNames={remarkSelectedClassNames}
            itemTextClassName={itemTextClassName}
            focusOptionClassNames={focusOptionClassNames}
            onLoadedZoneCoincidencesService={onLoadedZoneCoincidencesService}
          />
        ) : null}

        <div hidden={!isInitialized}>
          <CoincidencesList
            showResults={showResults}
            selectedIndex={selectedIndex}
            totalZones={totalZones}
            locations={locations}
            focusOptionClassNames={focusOptionClassNames}
            remarkSelectedClassNames={remarkSelectedClassNames}
            withRemarkSelected={withRemarkSelected}
            itemClassName={itemClassName}
            itemTextClassName={itemTextClassName}
            isVisible={isInitialized && Boolean(isVisibledResultsContainer)}
            searchText={searchText}
            onSelected={(place) => {
              onSelected({
                ...place,
                userInput: searchText
              })
              setSelectedIndex(-1)
            }}
            onLoadedCoincidences={onLoadedCoincidences}
            setTotalCoincidences={setTotalCoincidences}
            validateStreetNumber={validateStreetNumber}
            limitedRegions={limitedRegions}
            placeTypes={placeTypes}
            onLoadedCoincidencesService={onLoadedCoincidencesService}
          />
        </div>

        {enableDefaultSearches && (
          <DefaultSearches
            isHidden={!isVisibledResultsContainer}
            selectedIndex={selectedIndex}
            searchText={searchText}
            itemClassName={itemClassName}
            itemTextClassName={itemTextClassName}
            focusOptionClassNames={focusOptionClassNames}
            onSelected={(place) => {
              onSelected(place)
              setSelectedIndex(-1)
            }}
          />
        )}
      </div>
    </div>
  )
}

export default ResultsContainer
